import React from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Divider,
  Text,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDownSmall';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUpSmall';
import Delete from 'wix-ui-icons-common/on-stage/DeleteSmall';
import { classes, st } from './ParticipantSection.st.css';
import { ParticipantSectionDataHooks } from './dataHooks';
import {
  getParticipantHeading,
  getParticipantLabel,
} from '../../../../utils/multiParticipantFlow';

export interface ParticipantSectionProps {
  participantIndex: number;
  totalParticipants: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
  // Participant's name once the form has one; shown as "Participant N: {name}".
  title?: string;
  children: React.ReactNode;
}

/**
 * One participant's slice of the booking form (form, price variant, add-ons, ...).
 *
 * With a single participant it renders its children as-is, so the form looks exactly like
 * today's. With several participants each section gets a header ("Participant N: name",
 * Remove, chevron) and collapses like an accordion. The body stays mounted while collapsed so its
 * form-viewer ref remains available for validation.
 */
const ParticipantSection = React.forwardRef<
  HTMLDivElement,
  ParticipantSectionProps
>(
  (
    {
      participantIndex,
      totalParticipants,
      isExpanded,
      onToggle,
      onRemove,
      title,
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const removeText = t(
      'bookings.form.collectParticipant.removeParticipant.text',
    );
    const { isMobile, isRTL } = useEnvironment();
    const showHeader = totalParticipants > 1;
    const bodyId = `participant-section-body-${participantIndex}`;
    const titleId = `participant-section-title-${participantIndex}`;
    const ToggleIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
      <div
        ref={ref}
        className={st(classes.root, {
          isMobile,
          isRTL,
          withHeader: showHeader,
        })}
        data-hook={`${ParticipantSectionDataHooks.ROOT}-${participantIndex}`}
      >
        {showHeader && <Divider className={classes.divider} />}
        {showHeader && (
          <div
            className={classes.header}
            data-hook={ParticipantSectionDataHooks.HEADER}
          >
            <button
              type="button"
              className={classes.toggle}
              data-hook={ParticipantSectionDataHooks.TOGGLE}
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
            >
              <Text
                id={titleId}
                className={classes.title}
                data-hook={ParticipantSectionDataHooks.TITLE}
              >
                {getParticipantHeading(t, participantIndex + 1, title)}
              </Text>
            </button>
            {onRemove && (
              <TextButton
                className={classes.remove}
                priority={TextButtonPriority.secondary}
                prefixIcon={<Delete />}
                // Built from the visible word plus the participant, so the accessible
                // name always contains what is on screen (WCAG 2.5.3) whatever the
                // translations turn out to be, and it says which participant.
                aria-label={`${removeText} ${getParticipantLabel(
                  t,
                  participantIndex + 1,
                )}`}
                data-hook={ParticipantSectionDataHooks.REMOVE}
                onClick={onRemove}
              >
                {removeText}
              </TextButton>
            )}
            {/* A second control for the same toggle: visitors click the chevron, so it
                cannot be a click target hidden from assistive tech either. It borrows
                the title's name and stays out of the tab order, since the title button
                ahead of it already does the job for the keyboard. */}
            <button
              type="button"
              tabIndex={-1}
              className={classes.chevron}
              data-hook={ParticipantSectionDataHooks.CHEVRON}
              onClick={onToggle}
              aria-labelledby={titleId}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
            >
              <ToggleIcon />
            </button>
          </div>
        )}
        <div
          id={bodyId}
          className={classes.body}
          data-hook={ParticipantSectionDataHooks.BODY}
          hidden={showHeader && !isExpanded}
        >
          {children}
        </div>
      </div>
    );
  },
);

ParticipantSection.displayName = 'ParticipantSection';

export default ParticipantSection;
